import type { Operation } from "@apollo/client";
import { ApolloLink, Observable } from "@apollo/client";
import * as Sentry from "@sentry/react";

/**
 * Custom Apollo Link that creates Sentry spans for GraphQL operations
 */
export const createSentryApolloLink = () => {
  return new ApolloLink((operation, forward) => {
    const operationName = operation.operationName || "UnnamedOperation";
    const operationDef = operation.query.definitions.find(
      (def): def is import("graphql").OperationDefinitionNode =>
        def.kind === "OperationDefinition",
    );
    const operationType = operationDef?.operation || "query";

    const span = Sentry.startInactiveSpan({
      name: `${operationType}: ${operationName}`,
      op: "graphql.operation",
      attributes: {
        "graphql.operation.name": operationName,
        "graphql.operation.type": operationType,
      },
    });

    return new Observable((observer) => {
      const sub = forward(operation).subscribe({
        next: (result) => {
          span?.end();
          if (result.errors) {
            for (const error of result.errors) {
              Sentry.captureException(error, {
                contexts: {
                  graphql: {
                    operationName,
                    operationType,
                    variables: operation.variables,
                  },
                },
              });
            }
          }
          observer.next(result);
        },
        error: (error) => {
          span?.end();
          observer.error(error);
        },
        complete: () => {
          span?.end();
          observer.complete();
        },
      });

      return () => {
        sub.unsubscribe();
      };
    });
  });
};
